import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2 } from 'lucide-react';
import {
  api,
  AdoptionDateRange,
  AdoptionOverviewDto,
  AdoptionContributorDto,
  AdoptionConsumerDto,
  AdoptionTrendPointDto,
  UserAdoptionEventDto,
} from '../../services/api';

interface AdoptionPanelProps {
  isAdmin: boolean;
}

interface AdminData {
  contributors: AdoptionContributorDto[];
  consumers: AdoptionConsumerDto[];
  trends: AdoptionTrendPointDto[];
}

function toDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultRange(): AdoptionDateRange {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 90);
  return { startDate: toDateInput(start), endDate: toDateInput(end) };
}

function formatPeriod(periodStart: string): string {
  return new Date(periodStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTimestamp(timestamp: string): string {
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function AdoptionPanel({ isAdmin }: AdoptionPanelProps) {
  const [range, setRange] = useState<AdoptionDateRange>(defaultRange);
  const [interval, setInterval] = useState<'week' | 'month'>('week');
  const [overview, setOverview] = useState<AdoptionOverviewDto | null>(null);
  const [adminData, setAdminData] = useState<AdminData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-user drill-down (admin only)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [history, setHistory] = useState<UserAdoptionEventDto[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const rangeInvalid = !!range.startDate && !!range.endDate && range.startDate > range.endDate;

  const loadData = async () => {
    if (rangeInvalid) return;
    try {
      setLoading(true);
      setError(null);
      // The date range drives every sub-view; one failure fails the panel as a unit.
      const overviewPromise = api.getAdoptionOverview(range);
      if (isAdmin) {
        const [overviewResult, contributors, consumers, trends] = await Promise.all([
          overviewPromise,
          api.getAdoptionContributors(range),
          api.getAdoptionConsumers(range),
          api.getAdoptionTrends(range, interval),
        ]);
        setOverview(overviewResult);
        setAdminData({ contributors, consumers, trends });
      } else {
        setOverview(await overviewPromise);
        setAdminData(null);
      }
    } catch (err) {
      console.error('Failed to load adoption analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load adoption analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.startDate, range.endDate, interval, isAdmin]);

  const openDrilldown = async (userId: string) => {
    setSelectedUserId(userId);
    setHistory(null);
    setHistoryError(null);
    try {
      setHistoryLoading(true);
      setHistory(await api.getUserAdoptionHistory(userId, range));
    } catch (err) {
      console.error('Failed to load user adoption history:', err);
      setHistoryError(err instanceof Error ? err.message : 'Failed to load user adoption history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Drill-down history is scoped to the same range as everything else.
  useEffect(() => {
    if (selectedUserId) {
      openDrilldown(selectedUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.startDate, range.endDate]);

  const trendChartData = adminData?.trends.map((t) => ({
    ...t,
    period: formatPeriod(t.periodStart),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Adoption</CardTitle>
        <CardDescription>
          {isAdmin
            ? 'Individual contribution and consumption across the library for the selected date range'
            : 'Aggregate adoption activity across the library for the selected date range'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-8">
          {/* Date range picker — drives every sub-view below */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <Label htmlFor="adoption-start-date">From</Label>
              <Input
                id="adoption-start-date"
                type="date"
                value={range.startDate}
                onChange={(e) => setRange((r) => ({ ...r, startDate: e.target.value }))}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="adoption-end-date">To</Label>
              <Input
                id="adoption-end-date"
                type="date"
                value={range.endDate}
                onChange={(e) => setRange((r) => ({ ...r, endDate: e.target.value }))}
                className="w-40"
              />
            </div>
            {isAdmin && (
              <div className="flex rounded-md border overflow-hidden">
                {(['week', 'month'] as const).map((option) => (
                  <button
                    key={option}
                    onClick={() => setInterval(option)}
                    className={`px-3 py-2 text-sm capitalize ${
                      interval === option
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background hover:bg-muted'
                    }`}
                  >
                    {option}ly
                  </button>
                ))}
              </div>
            )}
          </div>

          {rangeInvalid ? (
            <Alert variant="destructive">
              <AlertDescription>The start date must be on or before the end date.</AlertDescription>
            </Alert>
          ) : loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertDescription>
                Error loading adoption analytics: {error}
                <button onClick={loadData} className="ml-4 underline hover:no-underline">
                  Try again
                </button>
              </AlertDescription>
            </Alert>
          ) : !overview ? (
            <Alert>
              <AlertDescription>No adoption data available</AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Aggregate overview — the only sub-view non-admin users see */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-3 rounded border">
                  <p className="text-xs text-muted-foreground">Prompts Created</p>
                  <p className="text-2xl font-bold">{overview.totalPromptsCreated}</p>
                </div>
                <div className="p-3 rounded border">
                  <p className="text-xs text-muted-foreground">Views + Copies</p>
                  <p className="text-2xl font-bold">{overview.totalViewsAndCopies}</p>
                </div>
                <div className="p-3 rounded border">
                  <p className="text-xs text-muted-foreground">Active Contributors</p>
                  <p className="text-2xl font-bold">{overview.activeContributors}</p>
                </div>
                <div className="p-3 rounded border">
                  <p className="text-xs text-muted-foreground">Active Consumers</p>
                  <p className="text-2xl font-bold">{overview.activeConsumers}</p>
                </div>
              </div>

              {isAdmin && adminData && (
                selectedUserId ? (
                  /* Per-user drill-down */
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setSelectedUserId(null)}
                        className="text-sm underline hover:no-underline"
                      >
                        ← Back to adoption overview
                      </button>
                      <h4 className="text-sm font-medium text-muted-foreground">
                        Event history for {selectedUserId}
                      </h4>
                    </div>
                    {historyLoading ? (
                      <div className="flex items-center justify-center h-24">
                        <Loader2 className="h-6 w-6 animate-spin" />
                      </div>
                    ) : historyError ? (
                      <Alert variant="destructive">
                        <AlertDescription>
                          Error loading user history: {historyError}
                          <button
                            onClick={() => openDrilldown(selectedUserId)}
                            className="ml-4 underline hover:no-underline"
                          >
                            Try again
                          </button>
                        </AlertDescription>
                      </Alert>
                    ) : !history || history.length === 0 ? (
                      <Alert>
                        <AlertDescription>No events for this user in the selected range</AlertDescription>
                      </Alert>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                        {history.map((event) => (
                          <div
                            key={`${event.timestamp}-${event.eventType}-${event.promptId ?? 'none'}`}
                            className="flex items-center justify-between p-2 rounded border"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-medium px-2 py-0.5 rounded bg-muted">
                                {event.eventType}
                              </span>
                              <span className="text-sm">{event.promptTitle ?? '—'}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {formatTimestamp(event.timestamp)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Active users trend */}
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-3">
                        Active Contributors vs Consumers ({interval}ly)
                      </h4>
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={trendChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="period" stroke="#64748b" fontSize={10} angle={-45} textAnchor="end" height={50} />
                            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="activeContributors"
                              stroke="#3b82f6"
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              name="Contributors"
                            />
                            <Line
                              type="monotone"
                              dataKey="activeConsumers"
                              stroke="#10b981"
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              name="Consumers"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Top contributors */}
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-3">Top Contributors</h4>
                        {adminData.contributors.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No contributors in the selected range</p>
                        ) : (
                          <div className="space-y-2">
                            {adminData.contributors.map((contributor) => (
                              <div
                                key={contributor.userId}
                                className="flex items-center justify-between p-2 rounded border"
                              >
                                <button
                                  onClick={() => openDrilldown(contributor.userId)}
                                  title={contributor.userId}
                                  className="text-sm font-medium underline hover:no-underline text-left"
                                >
                                  {contributor.displayName || contributor.email || contributor.userId}
                                </button>
                                <span className="text-xs text-muted-foreground">
                                  {contributor.contributionCount} contribution{contributor.contributionCount === 1 ? '' : 's'}
                                  {' · avg quality '}
                                  {contributor.averageQualityScore == null
                                    ? '—'
                                    : `${contributor.averageQualityScore.toFixed(1)}/10`}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Top consumers */}
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-3">Top Consumers</h4>
                        {adminData.consumers.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No consumers in the selected range</p>
                        ) : (
                          <div className="space-y-2">
                            {adminData.consumers.map((consumer) => (
                              <div
                                key={consumer.userId}
                                className="flex items-center justify-between p-2 rounded border"
                              >
                                <button
                                  onClick={() => openDrilldown(consumer.userId)}
                                  title={consumer.userId}
                                  className="text-sm font-medium underline hover:no-underline text-left"
                                >
                                  {consumer.displayName || consumer.email || consumer.userId}
                                </button>
                                <span className="text-xs text-muted-foreground">
                                  {consumer.views} view{consumer.views === 1 ? '' : 's'} · {consumer.copies}{' '}
                                  {consumer.copies === 1 ? 'copy' : 'copies'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
